// 公開パッケージ構築の純粋ロジック（ブラウザ完結版 backend から使用。Node 依存なし）。
// アップロード対象の選定・生成ゲーム向け runtime-config（TURN 既定適用）・最小 cache-manifest。
import { CONTENT_TYPES } from '../lib/publish-core.mjs';
import { applyPublicGameTurnDefaults } from '../lib/turn-defaults.mjs';
import { applyPublicGameFederationDefaults } from '../lib/federation-defaults.mjs';

/** 文字列 / Uint8Array のバイト数（ブラウザ・Node 両対応）。 */
export const contentByteLength = (data) => {
    if (typeof data === 'string') return new TextEncoder().encode(data).length;
    if (data instanceof Uint8Array) return data.byteLength;
    return 0;
};

const PUBLISH_PATH_PATTERN = /^(maps|cast)\/[A-Za-z0-9_\-][A-Za-z0-9_\-./]*$/;

/**
 * ゲームディレクトリ配下のファイル一覧から、公開アップロード対象を選定する。
 * maps/** と cast/** のうち、公開 API の拡張子許可リストに一致するものだけを対象にし、
 * 対象外（実行スクリプト・メタファイル等）は skipped として返す（silent drop にしない）。
 * @param {Array<{path: string, bytes: number}>} entries
 */
export function collectPublishFiles(entries) {
    const files = [];
    const skipped = [];
    for (const e of entries ?? []) {
        const p = String(e?.path ?? '');
        const ext = p.slice(p.lastIndexOf('.')).toLowerCase();
        const name = p.slice(p.lastIndexOf('/') + 1);
        const eligible = PUBLISH_PATH_PATTERN.test(p)
            && !p.includes('..') && !p.includes('//') && !name.startsWith('.')
            && Object.prototype.hasOwnProperty.call(CONTENT_TYPES, ext)
            && Number.isInteger(e?.bytes) && e.bytes > 0;
        if (eligible) files.push({ path: p, size: e.bytes });
        else skipped.push(p);
    }
    files.sort((a, b) => a.path.localeCompare(b.path));
    return { files, skipped, totalBytes: files.reduce((a, f) => a + f.size, 0) };
}

/**
 * 生成アセット（Meshy の GLB。IndexedDB assetFiles）のキーを公開パッケージ内のパスへ変換する。
 * `newgames/<gameId>/models/**` → `cast/models/**`（計画書 §2 の公開レイアウト）。
 * 対象外のキーは null を返す。
 */
export function toPackageAssetPath(assetPath, gameId) {
    const prefix = `newgames/${gameId}/`;
    const p = String(assetPath ?? '');
    if (!p.startsWith(prefix)) return null;
    const rest = p.slice(prefix.length);
    if (!rest || rest.includes('..')) return null;
    return `cast/${rest}`;
}

/**
 * ゲームデータ内の生成アセット参照を、公開パッケージ内のパスへ書き換える。
 * 例: `../assets/newgames/<gameId>/models/x.glb` → `cast/models/x.glb`
 *
 * エンジンは `assets/` を**共有CDN**（全ゲーム共通アセット）へ、`cast/` を**ゲーム別CDN**
 * （games-cdn の rev 込みベース）へ解決する（sevenfold/js/core/asset-base.ts）。
 * 生成モデルはゲーム固有なので、公開時は必ず cast/ 側へ寄せる。ローカルのプレビュー用データは
 * `../assets/` のまま残す（書き換えるのは公開パッケージの複製だけ）。
 */
export function rewriteGeneratedAssetRefs(text, gameId) {
    if (typeof text !== 'string') return text;
    // 「../」つきを先に処理する（後段で 'cast/' を作ると相対深さが崩れるため）
    return text
        .split(`../assets/newgames/${gameId}/`).join('cast/')
        .split(`/assets/newgames/${gameId}/`).join('cast/')
        .split(`assets/newgames/${gameId}/`).join('cast/');
}

// 開始画面素材（背景・立ち絵）はゲーム直下に置かれるため、公開パッケージでは cast/ 配下へ移す
// （maps/ と cast/ 以外は公開対象にならず、cast/ ならゲーム別CDNへ解決される）
const START_SCREEN_FILE_PATTERN = /^start-menu-[A-Za-z0-9_-]+\.(png|jpg|jpeg|webp)$/i;
export const START_SCREEN_PACKAGE_BASE = 'cast/start-screen/';

/** ゲーム直下の開始画面素材を公開パッケージ内のパスへ変換する（対象外は null）。 */
export function toPackageStartScreenPath(gamePath) {
    return START_SCREEN_FILE_PATTERN.test(String(gamePath ?? '')) ? START_SCREEN_PACKAGE_BASE + gamePath : null;
}

/**
 * ゲームディレクトリ配下のファイルと生成アセットを、公開パッケージ内のパスへ揃えて 1 本の一覧にする。
 *
 * 公開の実体（buildPublishPackage）と容量表示の見積もり（ホームの「ゲーム」欄）が
 * 同じ対象を数えるよう、パスの読み替えはここ 1 か所だけに置く。
 * 読み替えを片方だけに書くと、同じゲームなのに画面ごとに違う容量が出る
 *（2026-07-26 実障害: 開始画面素材が公開パッケージにだけ入り、ホームの見積もりから丸ごと落ちていた）。
 *
 * - ゲーム直下の開始画面素材 `start-menu-*.png` → `cast/start-screen/**`
 * - 生成3Dモデル `newgames/<gameId>/models/**` → `cast/models/**`（対象外のキーは捨てる）
 * - 同じパスへ来たものは先勝ち（＝ゲーム側を優先し、二重に数えない）
 *
 * @param {object} params
 * @param {Array<{path: string, bytes: number}>} params.gameFiles ゲームディレクトリ配下（bytes 以外の項目はそのまま持ち回る）
 * @param {Array<{path: string, bytes: number}>} params.assets 生成アセット（assets/ 相対の `newgames/<gameId>/…`）
 * @param {string} params.gameId
 * @returns {Array<{path: string, bytes: number}>}
 */
export function toPackageEntries({ gameFiles = [], assets = [], gameId }) {
    const entries = [];
    const seen = new Set();
    const add = (entry, path) => {
        if (!path || seen.has(path)) return;
        seen.add(path);
        entries.push({ ...entry, path });
    };
    for (const file of gameFiles) add(file, toPackageStartScreenPath(file?.path) ?? file?.path);
    for (const asset of assets) add(asset, toPackageAssetPath(asset?.path, gameId));
    return entries;
}

/**
 * 公開パッケージ（アップロード対象）を組み立てる。
 * ゲームディレクトリ配下（maps/**・cast/**）に加えて、**生成アセット（GLB）を cast/models/** として同梱**する。
 * 同梱しないと、公開したゲームがキャラの3Dモデルを共有CDNへ探しにいって 404 になる。
 * @param {object} params
 * @param {Array<{path: string, data: string|Uint8Array}>} params.gameFiles ゲームディレクトリ配下の実体
 * @param {Array<{path: string, data: string|Uint8Array}>} params.assets IndexedDB assetFiles（newgames/<gameId>/…）
 * @param {string} params.gameId
 * @returns {{files: Array<{path: string, size: number}>, skipped: string[], totalBytes: number, dataByPath: Map<string, string|Uint8Array>}}
 */
export function buildPublishPackage({ gameFiles = [], assets = [], gameId }) {
    // テキストは生成アセット参照を書き換えてから採寸する（サイズは finalize の HEAD 照合に使う）
    const withData = (file) => {
        const data = typeof file.data === 'string' ? rewriteGeneratedAssetRefs(file.data, gameId) : file.data;
        return { path: file.path, bytes: contentByteLength(data), data };
    };
    const entries = toPackageEntries({
        gameFiles: gameFiles.map(withData),
        assets: assets.map((asset) => ({ path: asset.path, bytes: contentByteLength(asset.data), data: asset.data })),
        gameId,
    });
    const dataByPath = new Map(entries.map((e) => [e.path, e.data]));
    const { files, skipped, totalBytes } = collectPublishFiles(entries);
    const selected = new Set(files.map((f) => f.path));
    for (const path of [...dataByPath.keys()]) if (!selected.has(path)) dataByPath.delete(path);
    return { files, skipped, totalBytes, dataByPath };
}

/**
 * 生成ゲーム向け runtime-config を共有ランタイムの既定から構築する。
 * scaffold と同じ既定を適用する:
 *   - TURN 既定（turnCredentials 削除＋iceServers STUN のみ＝計画書 §22.4-2）
 *   - 訪問受け入れ既定（shellOrigins をプラットフォーム既定へ固定・allowLoopbackShell を無効）
 * 入力は常に共有ランタイムの既定なので、ゲームがいつ作られたかに関わらず現行の既定が入る。
 * @param {string} runtimeConfigText 共有ランタイムの runtime-config.json 本文
 * @returns {string} 公開ゲームへ同梱する JSON 本文
 */
export function buildPublishRuntimeConfig(runtimeConfigText) {
    let config;
    try {
        config = JSON.parse(runtimeConfigText);
    } catch {
        throw new Error('共有ランタイムの runtime-config.json を解析できません');
    }
    applyPublicGameTurnDefaults(config);
    applyPublicGameFederationDefaults(config);
    return `${JSON.stringify(config, null, 2)}\n`;
}

/**
 * cache-manifest.json が無いプロジェクト向けの最小構成を生成する。
 * gameBaseUrl / dataCache.version は配信時に game-files Function が注入・上書きするため、
 * ここでは構造だけを用意する（playerModelCache はプレイアブルの SW キャッシュ warmup 用）。
 */
export function buildMinimalCacheManifest(gameId, coreCharacterIds = []) {
    return {
        formatVersion: '1.0',
        assetDelivery: { baseUrl: '', gameBaseUrl: '' },
        dataCache: { version: `${gameId}-local` },
        playerModelCache: {
            version: `${gameId}-models-v1`,
            coreCharacterIds: coreCharacterIds.filter((c) => typeof c === 'string' && c !== ''),
        },
    };
}
